import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    Grid,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { useApi } from '../hooks/useApi';
import { useSearch } from '../contexts/SearchContext';
import LaunchIcon from '@mui/icons-material/Launch';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface RemainingFile {
    name: string;
    url: string;
    lastModified: string;
}

interface ReportStatus {
    totalFiles: number;
    processedFiles: number;
    remainingFiles: number;
    lastUpdateTime: string;
    remainingFileList: RemainingFile[];
}

const ReportStatus: React.FC = () => {
    const [status, setStatus] = useState<ReportStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { callApi } = useApi();
    const { availableLocations, selectedLocation, setSelectedLocation, locationLoading } = useSearch();

    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Include location parameter if selected
            const locationParam = selectedLocation ? `?location=${encodeURIComponent(selectedLocation)}` : '';
            const response = await callApi(`/api/ReportStatus${locationParam}`);
            const data = await response.json();
            setStatus(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch status');
            console.error('Status fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [callApi, selectedLocation]);

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 10000); // Update every 10 seconds
        return () => clearInterval(interval);
    }, [fetchStatus]);

    const getProgressPercentage = () => {
        if (!status || status.totalFiles === 0) return 0;
        return (status.processedFiles / status.totalFiles) * 100;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Report Processing Status
            </Typography>

            {/* Location Selection */}
            {availableLocations.length > 1 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="location-select-label">Location</InputLabel>
                                <Select
                                    labelId="location-select-label"
                                    value={selectedLocation || ''}
                                    label="Location"
                                    onChange={(event) => setSelectedLocation(event.target.value)}
                                    startAdornment={<LocationOnIcon sx={{ mr: 1 }} />}
                                    disabled={locationLoading}
                                >
                                    {availableLocations.map((location) => (
                                        <MenuItem key={location} value={location}>
                                            {location}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {selectedLocation && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Showing status for: <strong>{selectedLocation}</strong>
                        </Typography>
                    )}
                </Paper>
            )}

            {/* Show current location if only one available */}
            {availableLocations.length === 1 && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon color="primary" />
                        <Typography variant="body1">
                            Location: <strong>{availableLocations[0]}</strong>
                        </Typography>
                    </Box>
                </Paper>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && !status ? (
                <CircularProgress />
            ) : status ? (
                <>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Processing Status
                                    </Typography>
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: 'background.paper', 
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Files Pending Processing
                                            </Typography>
                                            <Typography 
                                                variant="h3" 
                                                color={status.remainingFiles > 0 ? 'warning.main' : 'success.main'}
                                                sx={{ mb: 1 }}
                                            >
                                                {status.remainingFiles}
                                            </Typography>
                                            {status.remainingFiles === 0 && (
                                                <Typography variant="body1" color="success.main">
                                                    All files have been processed!
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                            Last updated: {new Date(status.lastUpdateTime).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Remaining Files ({status.remainingFileList.length})
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>File Name</TableCell>
                                            <TableCell>Last Modified</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {status.remainingFileList.map((file, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{file.name}</TableCell>
                                                <TableCell>
                                                    {new Date(file.lastModified).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: 'warning.main',
                                                            fontWeight: 'medium'
                                                        }}
                                                    >
                                                        Pending Processing
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        href={file.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        startIcon={<LaunchIcon />}
                                                    >
                                                        View File
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {status.remainingFileList.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography variant="body1" color="success.main">
                                                        All files have been processed successfully!
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </>
            ) : null}
        </Box>
    );
};

export default ReportStatus; 